import { HabitSchedule } from '@griply/types';
import { isAfter, startOfDay } from 'date-fns';

// Legacy clients could append a new schedule without capping the previous one,
// leaving an open-ended schedule that overlaps its successor. Cap every
// open-ended schedule at the startDate of the first schedule beginning on a
// later calendar day (endDate is exclusive, so its last active day becomes the
// day before the successor starts). Same-day siblings are left alone.
export const normalizeSchedules = <
  Schedule extends Pick<HabitSchedule, 'startDate' | 'endDate'>,
>(
  schedules: Schedule[],
): Schedule[] => {
  const lastStartTime = Math.max(
    ...schedules.map(({ startDate }) => startOfDay(startDate).getTime()),
  );
  const needsCapping = schedules.some(
    ({ endDate, startDate }) =>
      !endDate && startOfDay(startDate).getTime() < lastStartTime,
  );

  if (!needsCapping) {
    return schedules;
  }

  const byStartDate = [...schedules].sort(
    (scheduleA, scheduleB) =>
      scheduleA.startDate.getTime() - scheduleB.startDate.getTime(),
  );

  return byStartDate.map((schedule, index) => {
    if (schedule.endDate) {
      return schedule;
    }
    const successor = byStartDate
      .slice(index + 1)
      .find((other) =>
        isAfter(startOfDay(other.startDate), startOfDay(schedule.startDate)),
      );
    return successor ? { ...schedule, endDate: successor.startDate } : schedule;
  });
};
